// REUSABLE: Status Badge Component
// Displays status with consistent color coding. Used by: Timesheet, Tasks, Leave, Hiring, etc.
// Benefits: Consistent status display across app, Automatic color mapping, Easy to maintain status colors

import React from 'react';
import { Badge } from 'react-bootstrap';

export type StatusType = 
  | 'PENDING' 
  | 'APPROVED' 
  | 'REJECTED' 
  | 'CANCELLED'
  | 'DRAFT'
  | 'SUBMITTED'
  | 'IN_PROGRESS'
  | 'COMPLETED'
  | 'ON_HOLD'
  | 'BLOCKED'
  | 'ACTIVE'
  | 'INACTIVE'
  | string; // Allow custom statuses

interface StatusBadgeProps {
  status: StatusType; // Status value
  colorMap?: Record<string, string>; // Optional custom color mapping
  size?: 'sm' | 'lg'; // Badge size
  className?: string; // Additional className
}

// Default color mapping for common statuses
const DEFAULT_COLOR_MAP: Record<string, string> = {
  // Approval states
  PENDING: 'warning',
  APPROVED: 'success',
  REJECTED: 'danger',
  CANCELLED: 'secondary',
  
  // Submission states
  DRAFT: 'secondary',
  SUBMITTED: 'info',
  RESUBMITTED: 'warning',
  
  // Progress states
  IN_PROGRESS: 'primary',
  COMPLETED: 'success',
  ON_HOLD: 'warning',
  BLOCKED: 'danger',
  
  // General states
  ACTIVE: 'success',
  INACTIVE: 'secondary',
  
  // Leave statuses
  REVIEW: 'info',
  AUTO_REJECTED: 'danger',
  AUTO_APPROVED: 'success',
  
  // Task statuses
  TODO: 'secondary',
  DOING: 'primary',
  DONE: 'success',
  
  // Hiring statuses
  OPEN: 'success',
  CLOSED: 'secondary',
  UNDER_REVIEW: 'info',
  SCREENING_IN_PROGRESS: 'primary',
  SCREENING_PASSED: 'success',
  SCREENING_FAILED: 'danger',
  SELECTED: 'success',
  
  // Default fallback
  DEFAULT: 'secondary',
};

// Format status text for display (PENDING -> Pending, IN_PROGRESS -> In Progress)
function formatStatus(status: string): string {
  return status
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
}

// Status badge component with automatic color mapping
export const StatusBadge: React.FC<StatusBadgeProps> = ({
  status,
  colorMap,
  size,
  className = '',
}) => {
  const finalColorMap = { ...DEFAULT_COLOR_MAP, ...colorMap };
  const statusUpper = status.toUpperCase();
  const color = finalColorMap[statusUpper] || finalColorMap.DEFAULT;
  const displayText = formatStatus(status);

  return (
    <Badge 
      bg={color} 
      className={className}
      style={size === 'sm' ? { fontSize: '0.75rem' } : size === 'lg' ? { fontSize: '1rem' } : undefined}
    >
      {displayText}
    </Badge>
  );
};

export default StatusBadge;


